import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { useSelector } from 'react-redux';
import classes from './GlobalSearchandLocation.module.scss';
import MainInput from '../../common/Inputs/MainInput/MainInput';
import MainButton from '../../common/buttons/MainButton';
import colors from '../../../scss/colors';
import { LocationIcon, SearchJobIcon } from '../../common/Icons';
import { useWindowSize } from '../../../utils/hooks';
import { responsiveSizeJobs } from '../../../utils/constants';

export default function GlobalSearchandLocation({
  searchValue,
  setSearchValue,
  locationValue,
  setLocationValue,
  searchHandler,
  onKeyDownHandler,
  onKeyDownSearch,
  clearLocationHandler,
  clearSearchHandler,
  suggestionHandler,
  onFocusHandler,
  isSearchFocused,
  onBlurLocation,
  onBlurSearch,
  placeholderMobileHandler,
  placeholderMobile,
  locationRef,
  searchButtonVariant,
  custom = false,
  suggestions = false
}) {
  const { t } = useTranslation();
  const locations = useSelector((state) => state.locations.suggestions);
  const locationPending = useSelector((state) => state.locations.pendingLocation);
  const suggestion = useSelector((state) => state.jobs.suggestion);
  const pendingSuggestion = useSelector((state) => state.jobs.pendingSuggestion);
  const { width } = useWindowSize();
  const searchRef = useRef(null);
  const [showClear, setShowClear] = useState({
    search: false,
    location: false
  });

  const getRecentSearches = () => {
    const getItem = localStorage.getItem('userRecentSearches') || [];
    return getItem?.length ? JSON.parse(getItem) : null;
  };

  const constructData = () => {
    if (!searchValue) {
      return getRecentSearches();
    }
    return pendingSuggestion ? [] : suggestion;
  };

  const handleCustomRadius = () => {
    if (!isSearchFocused && width < responsiveSizeJobs) {
      return '10px';
    }

    if (width > responsiveSizeJobs) {
      return '10px 0px 0px 10px';
    }

    return '10px 10px 0 0';
  };

  useEffect(() => {
    if (!locationValue && !searchValue) {
      placeholderMobileHandler(false);
    }
  }, [locationValue, searchValue]);

  const shouldInputRender = () => {
    if (custom) return true;
    return (width < responsiveSizeJobs && placeholderMobile) || width > responsiveSizeJobs;
  };

  const handleFocusOnPlaceholderButton = () => {
    placeholderMobileHandler(true);
    setTimeout(() => {
      searchRef?.current?.focus();
    }, 100);
  };

  const isNarrow = width < responsiveSizeJobs;
  const customSearchRadius = isNarrow ? '10px' : '10px 0px 0px 10px';
  const customSearchBorderWidth = isNarrow ? '1px' : '1px 0px 1px 1px';
  const customLocationRadius = isNarrow ? '10px' : '0px';
  const customLocationBorderWidth = isNarrow ? '1px' : '1px 1px 1px 1px';

  return (
    <div
      className={`${custom ? classes.customContainer : classes.container} ${
        custom ? 'bg-white rounded-2xl items-end' : 'items-center'
      } ${custom && width < responsiveSizeJobs ? 'flex-col' : ''}`}
    >
      <div
        className={`${classes.containerColumns} ${
          custom ? 'bg-white px-4 pb-0 pt-4 md:pl-5 md:pr-0 md:pt-4 md:pb-4 rounded-tl-2xl rounded-bl-2xl gap-2' : ''
        }`}
      >
        {custom && (
          <div className={`flex flex-col gap-0.5 mb-1.5 ${!custom ? 'px-1' : ''}`}>
            <span className="text-md md:text-lg font-medium text-gray-500">Role Title</span>
            <span className="text-xs md:text-sm text-gray-500">Search via job title</span>
          </div>
        )}
        {width < responsiveSizeJobs && !placeholderMobile && !custom && (
          <Button
            sx={{
              background: colors.WHITE,
              fontWeight: 400,
              fontSize: '12px',
              color: colors.BLACK_TXT,
              justifyContent: 'left',
              textTransform: 'none',
              padding: '8px 10px 8px 18px'
            }}
            onClick={() => handleFocusOnPlaceholderButton()}
            startIcon={<SearchJobIcon fill={colors.GRAY_TXT} />}
          >
            {locationValue || searchValue
              ? `${searchValue || ''}, ${locationValue || ''}`
              : t('dashboard.jobs.searchinput')}
          </Button>
        )}
        {shouldInputRender() && (
          <MainInput
            suggestions={suggestions}
            key="search"
            changeHandler={setSearchValue}
            suggestionHandler={suggestionHandler}
            onInputHandler={onKeyDownSearch}
            onKeyDownHandler={(e) => {
              if (e.key === 'Enter') {
                searchHandler();
              }
            }}
            onFocusHandler={() => {
              onFocusHandler();
              setShowClear({ search: true });
            }}
            value={searchValue}
            size="medium"
            placeholder={
              custom
                ? t(
                    'dashboard.jobs.searchinputCustom',
                    'less specific terms will provide more results, e.g. engineer, project manager'
                  )
                : t('dashboard.jobs.searchinput')
            }
            forceWidth="100%"
            customRadius={custom ? customSearchRadius : handleCustomRadius()}
            customBorder={{
              borderStyle: 'solid',
              borderColor: custom ? colors.DARKEST_GREY : colors.PAGE_BACKGROUND,
              borderWidth: custom ? customSearchBorderWidth : '0 1px 0 0'
            }}
            data={constructData()}
            pendingData={pendingSuggestion}
            searchObj="Name"
            clearHandler={() => {
              clearSearchHandler();
            }}
            icon={<SearchJobIcon fill={colors.GRAY_TXT} />}
            onBlurHandler={() => {
              if (width < responsiveSizeJobs) {
                onBlurSearch();
                searchHandler();
              }
              setShowClear({ search: false });
            }}
            autoComplete
            clearable
            customPosition={width < responsiveSizeJobs && '46px !important'}
            inputRef={searchRef}
            forceOpenOption={placeholderMobile}
            showClear={showClear.search}
            multiLinePlaceholder={custom}
          />
        )}
      </div>
      {shouldInputRender() && (
        <div
          className={`${classes.containerColumns} ${
            custom ? 'bg-white py-4 px-4 md:pt-4 md:pb-4 md:px-0 rounded-tl-2xl rounded-bl-2xl gap-2' : ''
          }`}
        >
          {custom && (
            <div className={`flex flex-col gap-0.5 mb-1.5 ${!custom ? 'px-1' : ''}`}>
              <span className="text-md md:text-lg font-medium text-gray-500">Location</span>
              <span className="text-xs md:text-sm text-gray-500">Search by country, state or city</span>
            </div>
          )}
          <MainInput
            suggestions={suggestions}
            key="location"
            inputRef={locationRef}
            changeHandler={setLocationValue}
            onInputHandler={onKeyDownHandler}
            onKeyDownHandler={(e) => {
              if (e.key === 'Enter') {
                searchHandler();
              }
            }}
            value={locationValue}
            size="medium"
            placeholder={
              custom
                ? t(
                    'dashboard.jobs.locationCustom',
                    'single word search terms will provide more results, e.g. Mexico, Florida, Delhi'
                  )
                : t('dashboard.jobs.location')
            }
            forceWidth="100%"
            icon={<LocationIcon />}
            customRadius={custom ? customLocationRadius : handleCustomRadius()}
            customBorder={{
              borderStyle: 'solid',
              borderColor: custom ? colors.DARKEST_GREY : colors.PAGE_BACKGROUND,
              borderWidth: custom ? customLocationBorderWidth : '0 1px 0 0'
            }}
            data={locationPending ? [] : locations}
            pendingData={locationPending}
            searchObj="location"
            onFocusHandler={() => {
              setShowClear({ location: true });
            }}
            clearHandler={() => {
              clearLocationHandler();
            }}
            onBlurHandler={() => {
              if (width < responsiveSizeJobs) {
                onBlurLocation();
                searchHandler();
                placeholderMobileHandler(false);
              }
              setShowClear({ location: false });
            }}
            autoComplete
            clearable
            showClear={showClear.location}
            multiLinePlaceholder={custom}
          />
        </div>
      )}

      {(width > responsiveSizeJobs || custom) && (
        <div
          className={`${classes.containerButtons} ${custom ? 'pb-4 pr-5' : ''} ${
            custom && width < responsiveSizeJobs ? 'w-full px-4 pb-4' : ''
          }`}
        >
          <MainButton
            size="medium"
            variant={searchButtonVariant}
            customRadius={custom && width < responsiveSizeJobs ? '10px' : '0px 10px 10px 0px'}
            action={searchHandler}
            forceWidth={custom && width < responsiveSizeJobs ? '100%' : '128px'}
          >
            {t('dashboard.jobs.search')}
          </MainButton>
        </div>
      )}
    </div>
  );
}
